import { Navigate, Outlet, useLocation } from "react-router-dom";

import { AuthGuard } from "@/components/guards/AuthGuard";
import { ClinicSelectionScreen } from "@/components/layout/ClinicSelectionScreen";
import { EmailNotVerifiedScreen } from "@/components/layout/EmailNotVerifiedScreen";
import { Footer } from "@/components/layout/Footer";
import { AmountsUnlockDialog } from "@/components/layout/AmountsUnlockDialog";
import { IdleTimeoutModal } from "@/components/layout/IdleTimeoutModal";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { ConnectionBanner } from "@/components/layout/ConnectionBanner";
import { InAppShellContext } from "@/components/layout/PageFallback";
import { SupportBanner } from "@/components/layout/SupportBanner";
import { SystemAlertsBar } from "@/components/layout/SystemAlertsBar";
import { LicenseExpiredScreen } from "@/components/layout/LicenseExpiredScreen";
import { NavPageTabs } from "@/components/layout/NavPageTabs";
import { NavV3Notice } from "@/components/layout/NavV3Notice";
import { TopNavbar } from "@/components/layout/TopNavbar";
import { TopProgressBar } from "@/components/layout/TopProgressBar";
import { useAmountsStore } from "@/features/auth/amounts-store";
import { CascadeDeleteDialog } from "@/features/cascade/components/CascadeDeleteDialog";
import { useAuthStore } from "@/features/auth/store";
import { TourOverlay } from "@/features/help/components/TourOverlay";
import { WelcomeDialog } from "@/features/help/components/WelcomeDialog";
import { useHelpHydration } from "@/features/help/hooks";
import { useUnsavedQueueGuard } from "@/lib/api/offline-mutations";
import { useUiTemplateAttribute } from "@/lib/hooks/use-ui-template";
import { contentMaxWidthClass } from "@/lib/content-width";
import { useBranding } from "@/lib/splash-brand";
import { cn } from "@/lib/utils";

const PASSWORD_ROUTE = "/profil/password";

export function ProtectedLayout() {
  // Avertit avant fermeture tant qu'une écriture reste en file (mémoire seule).
  useUnsavedQueueGuard();
  // Mémoire de l'aide contextuelle (bulles, visites) du compte connecté.
  useHelpHydration();
  // Tient à jour le logo de l'écran de chargement (prochaine ouverture).
  useBranding();
  // Présentation E (module `ui_template_e`, ADR 0068) : `data-ui="e"` sur <html>.
  useUiTemplateAttribute();
  const me = useAuthStore((s) => s.me);
  const location = useLocation();
  // Bascule de confidentialité des montants : force le re-rendu de TOUTES les
  // pages (Outlet) quand elle change, pour que `formatMoney` (lu au rendu, pas
  // réactif par lui-même) reflète immédiatement le nouvel état.
  const amountsVisible = useAmountsStore((s) => s.visible);

  // Adresse e-mail jamais confirmée — addendum ADR 0033 (2026-09-30) : le
  // lien automatique de l'e-mail l'exigeait déjà, mais la connexion manuelle
  // (mot de passe) laissait toujours entrer. Bloque cette voie aussi, tant
  // qu'aucun lien n'a été cliqué. Seul `/profil` reste accessible (déconnexion) ;
  // `/editeur` aussi, le personnel plateforme étant déjà vérifié à l'invitation
  // (ADR 0033 point 4) — ce blocage ne le concerne donc jamais en pratique.
  if (
    me?.email_verified === false &&
    !location.pathname.startsWith("/profil") &&
    !location.pathname.startsWith("/editeur")
  ) {
    return (
      <AuthGuard>
        <EmailNotVerifiedScreen />
      </AuthGuard>
    );
  }

  // Première connexion / mot de passe temporaire : changement forcé, on empêche
  // d'aller ailleurs tant que ce n'est pas fait.
  if (me?.must_change_password && location.pathname !== PASSWORD_ROUTE) {
    return (
      <AuthGuard>
        <Navigate to={PASSWORD_ROUTE} replace />
      </AuthGuard>
    );
  }

  // Licence de la clinique active expirée : blocage (sauf staff éditeur, et sauf
  // la console éditeur / le profil pour régularisation et déconnexion).
  const lic = me?.license;
  const licenseBlocked = !!lic && !lic.is_valid && !me?.is_platform_staff;
  const allowWhenBlocked =
    location.pathname.startsWith("/editeur") || location.pathname.startsWith("/profil");
  if (licenseBlocked && !allowWhenBlocked) {
    return (
      <AuthGuard>
        <LicenseExpiredScreen />
      </AuthGuard>
    );
  }

  // Aucune clinique active (ex. superuser/éditeur multi-cliniques au login) :
  // on impose le choix d'une clinique avant toute page clinique. Sans cela, le
  // backend renvoie 403 et l'UI risquerait d'afficher des données périmées. La
  // console éditeur, le profil et la gestion des cliniques (page trans-cliniques
  // du plan éditeur) restent accessibles (ne dépendent pas d'une org).
  const needsClinic = !!me && !me.active_organisation_id;
  const allowWithoutClinic =
    location.pathname.startsWith("/editeur") ||
    location.pathname.startsWith("/profil") ||
    location.pathname.startsWith("/admin/organisations");
  if (needsClinic && !allowWithoutClinic) {
    // Le bandeau d'usurpation reste affiché : une cible multi-cliniques
    // atterrit ici, et « Revenir à mon compte » doit rester accessible.
    return (
      <AuthGuard>
        <ImpersonationBanner />
        <ClinicSelectionScreen />
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        Aller au contenu principal
      </a>
      <div className="flex min-h-screen flex-col bg-background">
        <TopProgressBar />
        <ConnectionBanner />
        <ImpersonationBanner />
        <SupportBanner />
        <SystemAlertsBar />
        <TopNavbar />
        <NavV3Notice />
        <main id="main" className={cn("mx-auto w-full flex-1 pb-12", contentMaxWidthClass(me))}>
          {/* Onglets des pages regroupées (menus simplifiés, ADR 0069). */}
          <NavPageTabs />
          <InAppShellContext.Provider value={true}>
            <Outlet key={amountsVisible ? "amounts-on" : "amounts-off"} />
          </InAppShellContext.Provider>
        </main>
        <Footer />
      </div>
      <IdleTimeoutModal />
      <AmountsUnlockDialog />
      <CascadeDeleteDialog />
      <WelcomeDialog />
      <TourOverlay />
    </AuthGuard>
  );
}
